<template>
    <dl :class="block(modifier(mutedLabels ? 'muted-labels' : ''), modifier(variant))">
        <div
            v-for="(item, index) in items"
            :key="`${item.label}-${index}`"
            :class="element('row', Array.isArray(item.value) ? 'stacked' : '')"
        >
            <dt :class="element('label')">
                <slot
                    name="label"
                    :item="item"
                    :index="index"
                    kind="item"
                >
                    {{ item.label }}
                </slot>
            </dt>
            <dd :class="element('value')">
                <slot
                    name="value"
                    :item="item"
                    :index="index"
                    kind="item"
                >
                    <ul
                        v-if="Array.isArray(item.value)"
                        :class="element('value-list')"
                    >
                        <li
                            v-for="(value, valueIndex) in item.value"
                            :key="valueIndex"
                        >
                            {{ value }}
                        </li>
                    </ul>
                    <template v-else>
                        {{ item.value }}
                    </template>
                </slot>
            </dd>
            <KButton
                v-if="item.editLabel"
                :class="element('edit')"
                :aria-label="item.editLabel"
                :label="editText"
                variant="ghost"
                size="sm"
                @click="emit('edit', { item, index, kind: 'item' })"
            />
        </div>

        <div :class="element('row', 'total')">
            <dt :class="element('label')">
                <slot
                    name="label"
                    :item="total"
                    :index="items.length"
                    kind="total"
                >
                    {{ total.label }}
                </slot>
            </dt>
            <dd :class="element('value')">
                <slot
                    name="value"
                    :item="total"
                    :index="items.length"
                    kind="total"
                >
                    {{ total.value }}
                </slot>
            </dd>
            <KButton
                v-if="total.editLabel"
                :class="element('edit')"
                :aria-label="total.editLabel"
                :label="editText"
                variant="ghost"
                size="sm"
                @click="emit('edit', { item: total, index: items.length, kind: 'total' })"
            />
        </div>

        <div
            v-if="trailing"
            :class="element('row', 'trailing')"
        >
            <dt :class="element('label')">
                <slot
                    name="label"
                    :item="trailing"
                    :index="items.length + 1"
                    kind="trailing"
                >
                    {{ trailing.label }}
                </slot>
            </dt>
            <dd :class="element('value')">
                <slot
                    name="value"
                    :item="trailing"
                    :index="items.length + 1"
                    kind="trailing"
                >
                    {{ trailing.value }}
                </slot>
            </dd>
            <KButton
                v-if="trailing.editLabel"
                :class="element('edit')"
                :aria-label="trailing.editLabel"
                :label="editText"
                variant="ghost"
                size="sm"
                @click="emit('edit', { item: trailing, index: items.length + 1, kind: 'trailing' })"
            />
        </div>
    </dl>
</template>

<script setup lang="ts">
import type { KLineItemEditEvent, KLineItemsProps, KLineItemsSlots } from './types'
import useBem from '@yeeroen/use-bem'
import KButton from '../button/k-button.vue'

defineOptions({ name: 'KLineItemsList' })

withDefaults(defineProps<KLineItemsProps>(), {
    items: () => [],
    trailing: undefined,
    mutedLabels: false,
    variant: 'default',
    editText: 'Edit',
})

const emit = defineEmits<{
    edit: [event: KLineItemEditEvent]
}>()

defineSlots<KLineItemsSlots>()

const { block, element, modifier } = useBem('k-line-items')
</script>
